// Copyright (C) 2021 Bystronic Laser AG

import { BEND_SUMMARY_BASE_COLOR } from '@/utils/color';
import { ChartGenerator, ProcedureName } from './abstract/chartGenerator';
import { PeakHourlyBendingData } from './chartsData';
import { FilterTimeAxisSpanEnum } from '../enums/FilterTimeAxisSpanEnum';
import { FilterTimeSpanEnum } from '../enums/FilterTimeSpanEnum';
import { metricsService } from '@/services/metrics.service';
import { devicesService } from '@/services/devices.service';
import { GeneratorParams } from '@/models/Charts/generatorParams';
import { BarSeriesOption, EChartsOption } from 'echarts';
import { abbreviateNumber } from '@/utils/number';
import { iterable } from '@/utils/array';
import { formatDateWithTimeOnCustomerTimeZone } from '@/utils/dates';
import { generatePalette } from '@/components/bend/kpiWidgetUtils';

/**
 * Chart generator for the 'Peak hourly bending' widget.
 *
 * This widget is placed in Work center console > Bend > Summary.
 *
 * Returns Echarts options to draw a bar chart with the 5 best bends/hour peaks
 * for each device for the specified time period.
 */
export class PeakHourlyBendingWidgetGenerator extends ChartGenerator<PeakHourlyBendingData> {
  constructor(procedure: ProcedureName, private customerIdDh: number) {
    super(procedure);
  }

  override async getData(
    selectedDevices: string[],
    selectedShifts: number[],
    timeSpan: FilterTimeSpanEnum | [string, string],
    timeAxisSpan?: FilterTimeAxisSpanEnum,
    params?: { [key: string]: any },
    customerNo?: string,
  ): Promise<PeakHourlyBendingData | null> {
    return metricsService.getBendingDevicesMetrics<PeakHourlyBendingData>(
      this.procedure,
      {
        customerIdDh: this.customerIdDh,
        deviceStringIds: selectedDevices,
        shiftIds: selectedShifts,
        timeSpan: timeSpan as FilterTimeSpanEnum,
      },
      this.controller,
    );
  }

  override updateOptions(
    data: PeakHourlyBendingData,
    parameters: GeneratorParams,
    prevOptions?: EChartsOption,
  ): EChartsOption {
    return {
      tooltip: {
        formatter: this.getTooltipFormatter(data),
      },
      grid: {
        top: 0,
        bottom: 20,
        left: 0,
        right: 0,
      },
      xAxis: {
        type: 'category',
        axisLine: {
          show: false,
        },
        axisTick: {
          show: false,
        },
        data: iterable(PEAKS_COUNT)
          .map((x) => x + 1)
          .reverse(),
      },
      yAxis: {
        type: 'value',
        show: false,
      },
      series: {
        type: 'bar',
        itemStyle: {
          borderRadius: 48,
        },
        barWidth: 16,
        data: this.getSeriesData(data),
      },
    };
  }

  private getSeriesData(data: PeakHourlyBendingData): BarSeriesOption[] {
    const palette = generatePalette(
      BEND_SUMMARY_BASE_COLOR,
      data.currentPeriodBestPeaks.length,
    );

    return this.addFillerEntriesUpToPeaksCount(
      data.currentPeriodBestPeaks.map((dataItem, index) => ({
        name: devicesService.store.findNameByStringId(dataItem.deviceStringId)!,
        value: dataItem.value,
        itemStyle: {
          color: palette[index],
        },
      })),
    );
  }

  /**
   * Adds filler series entries so the chart has always PEAKS_COUNT bars.
   *
   * This might happen when there are fewer data.currentPeriodBestPeaks entries
   * than PEAKS_COUNT.
   *
   * @param seriesData Series data generated from real data.
   */
  private addFillerEntriesUpToPeaksCount(seriesData: any[]): BarSeriesOption[] {
    const fillerEntries = iterable(PEAKS_COUNT - seriesData.length).map(() => ({
      name: FILLER_ENTRY_NAME,
      value: 0.1,
      itemStyle: {
        color: FILLER_BAR_COLOR,
      },
    }));

    return fillerEntries.concat(seriesData);
  }

  private getTooltipFormatter(data: PeakHourlyBendingData) {
    return (params: any) => {
      if (params.name === FILLER_ENTRY_NAME) {
        return '';
      }

      const fillerBarsCount = PEAKS_COUNT - data.currentPeriodBestPeaks.length;
      // If there are fewer entries in data.currentPeriodBestPeaks that
      // PEAKS_COUNT, there will be filler series entries (to fill up to
      // PEAKS_COUNT entries), so params.dataIndex has to be adjusted to
      // data.currentPeriodBestPeaks length.
      const adjustedIndex = params.dataIndex - fillerBarsCount;
      const date = new Date(
        data.currentPeriodBestPeaks[adjustedIndex].dateTime,
      );

      return `
        <span data-testid="date">
          ${formatDateWithTimeOnCustomerTimeZone(date)}
        </span>
        <hr style="margin: 4px 0;" />
        <span data-testid="device-name">${params.name}</span>:
           <b data-testid="value">
             ${abbreviateNumber(params.value, 2)}
           </b>
      `;
    };
  }
}

export const PEAKS_COUNT = 5;
export const FILLER_ENTRY_NAME = '##filler-bar-entry##';
export const FILLER_BAR_COLOR = '#afafaf';
